// s3Helper.js
//
// No AWS SDK and no AWS credentials here any more. Shipping an IAM key pair in
// the JS bundle gave every visitor read access to the screenshots bucket, so
// object access moved server-side:
//
//   blogs/*  → public marketing content. Served as a plain public URL, which
//              also lets the browser and CDN cache it. Presigned URLs expire
//              hourly and therefore never cache — bad for page speed and SEO.
//   anything → private employee data. Presigned by the backend, which checks
//   else       org + position visibility before signing.

import { SERVER_URL } from './serverConfig';

const AWS_REGION = process.env.REACT_APP_AWS_REGION || 'ap-south-1';
const AWS_BUCKET = process.env.REACT_APP_AWS_S3_BUCKET || 'teamgrid-134163282831';

/** Blog images live under this prefix and are world-readable by bucket policy. */
const PUBLIC_PREFIX = 'blogs/';

/** Plain, cacheable, non-expiring URL for a public object. */
function publicUrl(key) {
  return `https://${AWS_BUCKET}.s3.${AWS_REGION}.amazonaws.com/${key}`;
}

/**
 * Ask the backend to presign a private object (screenshots).
 * The caller's JWT is what authorises this — the key alone proves nothing,
 * the server resolves it to a screenshot and checks who may see it.
 */
async function getPrivateSignedUrl(key) {
  const token = localStorage.getItem('token');
  const res = await fetch(
    `${SERVER_URL}/api/v1/screenshots/signed-url?key=${encodeURIComponent(key)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {}, cache: 'no-store' }
  );
  if (!res.ok) throw new Error(`Failed to sign ${key}: ${res.status}`);
  const body = await res.json();
  return body?.data?.signedUrl || null;
}

/**
 * Resolve an S3 key to a usable URL.
 * Public blog keys resolve synchronously; private keys go through the backend.
 * Kept async so existing callers are unaffected.
 *
 * @param {string} key - S3 object key (e.g. 'blogs/x.webp', 'screenshots/y.webp')
 * @returns {Promise<string|null>}
 */
export async function getS3Url(key) {
  if (!key) return null;

  if (key.startsWith(PUBLIC_PREFIX)) {
    return publicUrl(key);
  }

  try {
    return await getPrivateSignedUrl(key);
  } catch (error) {
    console.error('Error getting signed URL for key:', key, error);
    return null;
  }
}

/**
 * Upload an image file to S3 using presigned URL
 * @param {File} file - The image file to upload
 * @param {string} uploadUrl - Presigned PUT URL from backend
 * @param {string} contentType - Content type to use for upload
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function uploadImageToS3(file, uploadUrl, contentType) {
  try {
    console.log('🔄 Uploading to S3:', {
      filename: file.name,
      contentType,
      fileSize: file.size,
      urlLength: uploadUrl.length
    });

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType,
      },
    });

    console.log('📡 S3 Upload Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries())
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error('❌ S3 Upload Error:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`Upload failed (${response.status}): ${errorText || response.statusText}`);
    }

    console.log('✅ S3 Upload Success');
    return { success: true };
  } catch (error) {
    console.error('❌ Error uploading image to S3:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    
    // Provide more specific error messages
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      console.error('🔍 CORS or Network Issue Detected');
      return { 
        success: false, 
        error: 'Network error: Unable to connect to S3. Please check: 1) CORS configuration on S3 bucket allows PUT from your domain, 2) Internet connection, 3) Browser console for detailed errors.' 
      };
    }
    return { success: false, error: error.message || 'Failed to upload image' };
  }
}

/**
 * Extract S3 key from presigned URL or return key as-is
 * @param {string} urlOrKey - Either a presigned URL or S3 key
 * @returns {string|null} - S3 key or null
 */
export function extractS3Key(urlOrKey) {
  if (!urlOrKey) return null;
  
  // If it's already a key (starts with 'blogs/'), return as-is
  if (urlOrKey.startsWith('blogs/')) {
    return urlOrKey;
  }
  
  // If it's a presigned URL, extract the key
  // Format: https://teamgrid.s3.ap-south-1.amazonaws.com/blogs/temp/xxx.webp?X-Amz-...
  if (urlOrKey.includes('s3.ap-south-1.amazonaws.com/')) {
    try {
      const url = new URL(urlOrKey);
      // Remove leading slash and get path
      const key = url.pathname.substring(1); // Remove first '/'
      return key;
    } catch (e) {
      return null;
    }
  }
  
  return null;
}

/**
 * Get fresh presigned URL from either a key or existing presigned URL
 * @param {string} urlOrKey - Either a presigned URL or S3 key
 * @param {number} expiresIn - URL expiration in seconds (default 3600 = 1 hour)
 * @returns {Promise<string|null>} - Fresh presigned URL or null
 */
export async function getFreshS3Url(urlOrKey) {
  if (!urlOrKey) return null;

  const s3Key = extractS3Key(urlOrKey);
  if (!s3Key) {
    // Not one of ours — probably an external image URL. Pass it through.
    return urlOrKey;
  }

  // Blog keys resolve to a public URL; anything else goes through the backend.
  // Values stored as old presigned URLs still work — extractS3Key strips them
  // back to a key first, and the result is idempotent.
  return getS3Url(s3Key);
}

/**
 * Upload blog image via backend (no CORS issues)
 * @param {File} file - The image file to upload
 * @param {string} blogId - Optional blog ID (for editing existing blog)
 * @param {Function} api - API instance from useApi hook
 * @returns {Promise<{success: boolean, key?: string, error?: string}>}
 */
export async function uploadBlogImage(file, blogId = null, api) {
  try {
    console.log('📤 Uploading blog image via backend:', {
      filename: file.name,
      fileSize: file.size,
      contentType: file.type,
      blogId
    });

    // Create FormData to send file
    const formData = new FormData();
    formData.append('image', file);
    if (blogId) {
      formData.append('blogId', blogId);
    }

    // Upload file through backend (backend will upload to S3)
    const response = await api.post('/api/v1/blogs/admin/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('✅ Upload response:', response.data);

    const { key } = response.data.data;

    if (!key) {
      console.error('❌ Invalid response: missing key', response.data);
      return { success: false, error: 'Invalid response from server: missing key' };
    }

    return { success: true, key };
  } catch (error) {
    console.error('❌ Error uploading blog image:', {
      error,
      response: error.response?.data,
      message: error.message
    });
    const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || error.message || 'Failed to upload image';
    return { 
      success: false, 
      error: errorMessage
    };
  }
}
